<template>
  <div class="page-content">
    <div class="activity-craft-management">
      <div class="page-header">
        <div class="header-info">
          <h2>活动制作管理</h2>
          <p class="header-desc">管理活动制作模板（仅管理员）</p>
        </div>
        <div class="header-actions">
          <el-select
            v-model="selectedGame"
            placeholder="选择游戏"
            style="width: 200px; margin-right: 10px;"
            @change="() => loadTemplates({ resetExpansion: true })"
          >
            <el-option label="尤弥尔传奇国际服" value="尤弥尔传奇国际服"></el-option>
            <el-option label="尤弥尔传奇韩服" value="尤弥尔传奇韩服"></el-option>
          </el-select>
          <el-button 
            v-if="templateTree.length > 0" 
            @click="toggleExpandAll" 
            style="margin-right: 10px;"
          >
            <i :class="isAllExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            {{ isAllExpanded ? '收起全部' : '展开全部' }}
          </el-button>
          <el-button type="primary" @click="showAddActivityDialog">
            <i class="el-icon-plus"></i>
            添加活动
          </el-button>
        </div>
      </div>

      <!-- 活动模板树 -->
      <div class="template-tree-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
        </div>
        <div v-else-if="!selectedGame" class="empty-state">
          <div class="empty-icon">🎮</div>
          <div class="empty-text">请先选择游戏</div>
        </div>
        <div v-else-if="templateTree.length === 0" class="empty-state">
          <div class="empty-icon">📋</div>
          <div class="empty-text">暂无活动模板</div>
          <div class="empty-desc">点击右上角"添加活动"按钮添加活动模板</div>
        </div>
        <div v-else class="template-tree">
          <el-collapse v-model="activeCollapseItems" accordion>
            <el-collapse-item
              v-for="activity in templateTree"
              :key="activity.id"
              :name="activity.id"
              class="activity-item"
            >
              <template #title>
                <div class="activity-header" @click.stop>
                  <span class="activity-name">{{ activity.name }}</span>
                  <div class="activity-actions" @click.stop>
                    <el-button
                      type="text"
                      size="small"
                      icon="el-icon-edit"
                      @click="showEditActivityDialog(activity)"
                    >
                      编辑
                    </el-button>
                    <el-button
                      type="text"
                      size="small"
                      icon="el-icon-plus"
                      @click="showAddMenuDialog(activity)"
                    >
                      添加菜单
                    </el-button>
                    <el-button
                      type="text"
                      size="small"
                      icon="el-icon-delete"
                      style="color: #f56c6c;"
                      @click="deleteActivityConfirm(activity)"
                    >
                      删除
                    </el-button>
                  </div>
                </div>
              </template>
              <div class="menus-list">
                <!-- 二级菜单列表 -->
                <el-collapse v-model="activeMenuItems" accordion>
                  <el-collapse-item
                    v-for="menu in activity.children || []"
                    :key="menu.id"
                    :name="menu.id"
                    class="menu-item"
                  >
                    <template #title>
                      <div class="menu-header" @click.stop>
                        <span class="menu-name">{{ menu.name }}</span>
                        <div class="menu-actions" @click.stop>
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-edit"
                            @click="showEditMenuDialog(menu)"
                          >
                            编辑
                          </el-button>
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-plus"
                            @click="showAddProductDialog(menu)"
                          >
                            添加商品
                          </el-button>
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-delete"
                            style="color: #f56c6c;"
                            @click="deleteMenuConfirm(menu)"
                          >
                            删除
                          </el-button>
                        </div>
                      </div>
                    </template>
                    <div class="products-list">
                      <div
                        v-for="product in menu.children || []"
                        :key="product.id"
                        class="product-item"
                      >
                        <div class="product-info">
                          <span class="product-name">{{ product.name }}</span>
                        </div>
                        <div class="product-actions">
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-edit"
                            @click="showEditProductDialog(product)"
                          >
                            编辑
                          </el-button>
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-delete"
                            style="color: #f56c6c;"
                            @click="deleteProductConfirm(product)"
                          >
                            删除
                          </el-button>
                        </div>
                      </div>
                      <div v-if="!menu.children || menu.children.length === 0" class="no-products">
                        <div style="text-align: center; padding: 20px; color: #999;">
                          暂无商品，点击"添加商品"按钮添加商品
                        </div>
                      </div>
                    </div>
                  </el-collapse-item>
                </el-collapse>
                <div v-if="!activity.children || activity.children.length === 0" class="no-menus">
                  <div style="text-align: center; padding: 20px; color: #999;">
                    暂无菜单，点击"添加菜单"按钮添加菜单
                  </div>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- 添加/编辑活动对话框 -->
      <el-dialog
        :title="dialogTitle"
        v-model="dialogVisible"
        width="500px"
        @close="resetDialog"
        class="custom-dialog"
      >
        <el-form :model="formData" label-width="80px" :rules="formRules" ref="formRef" size="default">
          <el-form-item label="名称" prop="name">
            <el-input v-model="formData.name" placeholder="请输入活动/商品名称" clearable style="width: 300px;"></el-input>
          </el-form-item>
          <el-form-item label="层级" prop="level">
            <el-radio-group v-model="formData.level" :disabled="isEditMode">
              <el-radio :label="1">一级活动</el-radio>
              <el-radio :label="2">二级菜单</el-radio>
              <el-radio :label="3">三级商品</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-if="formData.level === 2"
            label="所属活动"
            prop="parentId"
          >
            <el-select v-model="formData.parentId" placeholder="请选择所属活动" style="width: 300px;" filterable>
              <el-option
                v-for="activity in templateTree"
                :key="activity.id"
                :label="activity.name"
                :value="activity.id"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="formData.level === 3"
            label="所属菜单"
            prop="parentId"
          >
            <el-select v-model="formData.parentId" placeholder="请选择所属菜单" style="width: 300px;" filterable>
              <template v-for="activity in templateTree">
                <el-option
                  v-for="menu in activity.children || []"
                  :key="menu.id"
                  :label="`${activity.name} - ${menu.name}`"
                  :value="menu.id"
                ></el-option>
              </template>
            </el-select>
          </el-form-item>
          <el-form-item label="排序顺序">
            <el-input-number v-model="formData.sortOrder" :min="0" controls-position="right" style="width: 120px;"></el-input-number>
          </el-form-item>
          <el-form-item label="是否启用">
            <el-switch
              v-model="formData.isActive"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="禁用"
              inline-prompt
            ></el-switch>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveTemplate" :loading="saving">保存</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance, nextTick, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getActivityCraftTemplateTree,
  createActivityCraftTemplate,
  updateActivityCraftTemplate,
  deleteActivityCraftTemplate
} from '@/api/activityCraft'

export default {
  name: 'ActivityCraftManagement',
  setup() {
    const { proxy } = getCurrentInstance()
    const selectedGame = ref('')
    const templateTree = ref([])
    const loading = ref(false)
    const dialogVisible = ref(false)
    const dialogTitle = ref('添加活动')
    const isEditMode = ref(false)
    const saving = ref(false)
    const activeCollapseItems = ref([])
    const activeMenuItems = ref([])
    const materialInputVisible = ref(false)
    const isAllExpanded = ref(false)
    const materialInputValue = ref('')

    const formData = reactive({
      id: null,
      name: '',
      level: 1,
      parentId: null,
      searchAccuracy: 0.65,
      materialVerificationNames: [],
      useImageRecognition: 0,
      imagePath: '',
      sortOrder: 0,
      game: '',
      isActive: 1
    })

    const formRules = {
      name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
      level: [{ required: true, message: '请选择层级', trigger: 'change' }],
      parentId: [
        {
          required: true,
          message: '请选择所属活动',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (formData.level === 2 && !value) {
              callback(new Error('请选择所属活动'))
            } else {
              callback()
            }
          }
        }
      ],
      imagePath: [
        {
          required: true,
          message: '请输入图像路径',
          trigger: 'blur',
          validator: (rule, value, callback) => {
            if (formData.useImageRecognition === 1 && !value) {
              callback(new Error('使用图像识别时必须输入图像路径'))
            } else {
              callback()
            }
          }
        }
      ]
    }

    const normalizeTemplateNode = (node) => {
      const children = Array.isArray(node.children)
        ? node.children.map(child => normalizeTemplateNode(child))
        : []

      return {
        ...node,
        parentId: node.parentId ?? node.parent_id ?? null,
        searchAccuracy: node.searchAccuracy ?? node.search_accuracy ?? 0.65,
        materialVerificationNames: node.materialVerificationNames ?? node.material_verification_names ?? [],
        useImageRecognition: node.useImageRecognition ?? node.use_image_recognition ?? 0,
        imagePath: node.imagePath ?? node.image_path ?? '',
        sortOrder: node.sortOrder ?? node.sort_order ?? 0,
        isActive: node.isActive ?? node.is_active ?? 1,
        children
      }
    }

    // el-collapse 在 accordion 模式下 v-model 为 string | number，非 accordion 为数组；统一成数组再处理
    const collapseActiveToArray = (v) => {
      if (v == null || v === '') return []
      return Array.isArray(v) ? v.slice() : [v]
    }

    // 加载活动模板树；resetExpansion：切换游戏等场景重置为默认展开；否则尽量保留当前展开的活动/菜单
    const loadTemplates = async (options = {}) => {
      const resetExpansion = options.resetExpansion === true

      if (!selectedGame.value) {
        templateTree.value = []
        return
      }

      const prevActivity = collapseActiveToArray(activeCollapseItems.value)
      const prevMenu = collapseActiveToArray(activeMenuItems.value)
      const wasAllExpanded = isAllExpanded.value

      loading.value = true
      try {
        const response = await getActivityCraftTemplateTree(selectedGame.value)
        console.log('API响应:', response)
        // 响应拦截器已经返回了 response.data，所以 response 就是 ApiResponse 对象
        if (response && response.code === 200) {
          const data = response.data || []
          // 确保赋值的是数组，使用展开运算符创建新数组以确保响应式更新
          templateTree.value = Array.isArray(data)
            ? data.map(item => normalizeTemplateNode(item))
            : []

          const tree = templateTree.value
          if (tree.length === 0) {
            isAllExpanded.value = false
            activeCollapseItems.value = []
            activeMenuItems.value = []
          } else if (resetExpansion) {
            activeCollapseItems.value = [tree[0].id]
            activeMenuItems.value = []
            isAllExpanded.value = false
          } else if (wasAllExpanded) {
            activeCollapseItems.value = tree.map(a => a.id)
            const allMenuIds = []
            tree.forEach(activity => {
              (activity.children || []).forEach(menu => {
                allMenuIds.push(menu.id)
              })
            })
            activeMenuItems.value = allMenuIds
            isAllExpanded.value = true
          } else {
            const activityIds = new Set(tree.map(a => a.id))
            let nextActivity = []
            if (prevActivity.length === 0) {
              nextActivity = []
            } else {
              const id = prevActivity.find(x => activityIds.has(x))
              nextActivity = id != null ? [id] : [tree[0].id]
            }
            activeCollapseItems.value = nextActivity

            const menuIdSet = new Set()
            tree.forEach(a => (a.children || []).forEach(m => menuIdSet.add(m.id)))
            let nextMenu = []
            if (prevMenu.length === 0) {
              nextMenu = []
            } else {
              const mid = prevMenu.find(x => menuIdSet.has(x))
              nextMenu = mid != null ? [mid] : []
            }
            activeMenuItems.value = nextMenu
            isAllExpanded.value = false
          }

          await nextTick()
        } else {
          ElMessage.error(response?.message || '加载活动模板失败')
          templateTree.value = []
          activeCollapseItems.value = []
          activeMenuItems.value = []
          isAllExpanded.value = false
        }
      } catch (error) {
        console.error('加载活动模板错误:', error)
        ElMessage.error('加载活动模板失败: ' + (error.response?.data?.message || error.message))
        templateTree.value = []
        activeCollapseItems.value = []
        activeMenuItems.value = []
        isAllExpanded.value = false
      } finally {
        loading.value = false
      }
    }

    // 显示添加活动对话框
    const showAddActivityDialog = () => {
      if (!selectedGame.value) {
        ElMessage.warning('请先选择游戏')
        return
      }
      dialogTitle.value = '添加活动'
      isEditMode.value = false
      resetForm()
      formData.level = 1
      formData.game = selectedGame.value
      dialogVisible.value = true
    }

    // 显示添加菜单对话框（二级菜单）
    const showAddMenuDialog = (activity) => {
      dialogTitle.value = '添加菜单'
      isEditMode.value = false
      resetForm()
      formData.level = 2
      formData.parentId = activity.id
      formData.game = selectedGame.value
      dialogVisible.value = true
    }

    // 显示添加商品对话框（三级商品）
    const showAddProductDialog = (menu) => {
      dialogTitle.value = '添加商品'
      isEditMode.value = false
      resetForm()
      formData.level = 3
      formData.parentId = menu.id
      formData.game = selectedGame.value
      dialogVisible.value = true
    }

    // 显示编辑活动对话框
    const showEditActivityDialog = (activity) => {
      dialogTitle.value = '编辑活动'
      isEditMode.value = true
      Object.assign(formData, {
        id: activity.id,
        name: activity.name,
        level: activity.level,
        parentId: activity.parentId,
        searchAccuracy: activity.searchAccuracy,
        materialVerificationNames: activity.materialVerificationNames || [],
        useImageRecognition: activity.useImageRecognition,
        imagePath: activity.imagePath || '',
        sortOrder: activity.sortOrder || 0,
        game: activity.game,
        isActive: activity.isActive
      })
      dialogVisible.value = true
    }

    // 显示编辑菜单对话框（二级菜单）
    const showEditMenuDialog = (menu) => {
      dialogTitle.value = '编辑菜单'
      isEditMode.value = true
      Object.assign(formData, {
        id: menu.id,
        name: menu.name,
        level: menu.level,
        parentId: menu.parentId,
        searchAccuracy: menu.searchAccuracy,
        materialVerificationNames: menu.materialVerificationNames || [],
        useImageRecognition: menu.useImageRecognition,
        imagePath: menu.imagePath || '',
        sortOrder: menu.sortOrder || 0,
        game: menu.game,
        isActive: menu.isActive
      })
      dialogVisible.value = true
    }

    // 显示编辑商品对话框
    const showEditProductDialog = (product) => {
      dialogTitle.value = '编辑商品'
      isEditMode.value = true
      Object.assign(formData, {
        id: product.id,
        name: product.name,
        level: product.level,
        parentId: product.parentId,
        searchAccuracy: product.searchAccuracy,
        materialVerificationNames: product.materialVerificationNames || [],
        useImageRecognition: product.useImageRecognition,
        imagePath: product.imagePath || '',
        sortOrder: product.sortOrder || 0,
        game: product.game,
        isActive: product.isActive
      })
      dialogVisible.value = true
    }

    const formRef = ref(null)

    // 保存模板
    const saveTemplate = async () => {
      if (!formRef.value) return

      await formRef.value.validate(async (valid) => {
        if (!valid) return

        saving.value = true
        try {
          const data = { ...formData }
          // 确保 parentId / sortOrder 字段被正确传递（兼容后端期望的下划线命名）
          if (data.parentId !== null && data.parentId !== undefined) {
            data.parent_id = data.parentId
          }
          if (data.sortOrder !== null && data.sortOrder !== undefined) {
            data.sort_order = data.sortOrder
          }

          console.log('保存数据:', data)
          let response
          if (isEditMode.value) {
            response = await updateActivityCraftTemplate(data.id, data)
          } else {
            response = await createActivityCraftTemplate(data)
          }
          
          // 检查响应
          if (response && response.code === 200) {
            ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
            dialogVisible.value = false
            await loadTemplates()
          } else {
            ElMessage.error(response?.message || '保存失败')
          }
        } catch (error) {
          ElMessage.error('保存失败: ' + (error.response?.data?.message || error.message))
        } finally {
          saving.value = false
        }
      })
    }

    // 删除活动确认
    const deleteActivityConfirm = (activity) => {
      ElMessageBox.confirm(
        `确定要删除活动"${activity.name}"吗？删除活动将同时删除该活动下的所有商品。`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await deleteActivityCraftTemplate(activity.id)
          if (response && response.code === 200) {
            ElMessage.success('删除成功')
            await loadTemplates()
          } else {
            ElMessage.error(response?.message || '删除失败')
          }
        } catch (error) {
          ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }).catch(() => {})
    }

    // 删除菜单确认（二级菜单）
    const deleteMenuConfirm = (menu) => {
      ElMessageBox.confirm(
        `确定要删除菜单"${menu.name}"吗？删除菜单将同时删除该菜单下的所有商品。`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await deleteActivityCraftTemplate(menu.id)
          if (response && response.code === 200) {
            ElMessage.success('删除成功')
            await loadTemplates()
          } else {
            ElMessage.error(response?.message || '删除失败')
          }
        } catch (error) {
          ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }).catch(() => {})
    }

    // 删除商品确认（三级商品）
    const deleteProductConfirm = (product) => {
      ElMessageBox.confirm(
        `确定要删除商品"${product.name}"吗？`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await deleteActivityCraftTemplate(product.id)
          if (response && response.code === 200) {
            ElMessage.success('删除成功')
            await loadTemplates()
          } else {
            ElMessage.error(response?.message || '删除失败')
          }
        } catch (error) {
          ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }).catch(() => {})
    }

    // 重置表单
    const resetForm = () => {
      Object.assign(formData, {
        id: null,
        name: '',
        level: 1,
        parentId: null,
        searchAccuracy: 0.65,
        materialVerificationNames: [],
        useImageRecognition: 0,
        imagePath: '',
        sortOrder: 0,
        game: '',
        isActive: 1
      })
      materialInputVisible.value = false
      materialInputValue.value = ''
    }

    const materialInputRef = ref(null)

    // 重置对话框
    const resetDialog = () => {
      resetForm()
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    // 显示材料输入框
    const showMaterialInput = () => {
      materialInputVisible.value = true
      nextTick(() => {
        materialInputRef.value?.focus()
      })
    }

    // 添加材料名称
    const addMaterialName = () => {
      if (materialInputValue.value.trim()) {
        if (!formData.materialVerificationNames) {
          formData.materialVerificationNames = []
        }
        formData.materialVerificationNames.push(materialInputValue.value.trim())
        materialInputValue.value = ''
      }
      materialInputVisible.value = false
    }

    // 移除材料名称
    const removeMaterialName = (index) => {
      formData.materialVerificationNames.splice(index, 1)
    }

    // 展开/收起全部活动
    const toggleExpandAll = () => {
      if (isAllExpanded.value) {
        // 收起全部
        activeCollapseItems.value = []
        // 收起所有菜单
        activeMenuItems.value = []
        isAllExpanded.value = false
      } else {
        // 展开全部
        activeCollapseItems.value = templateTree.value.map(activity => activity.id)
        // 展开所有菜单
        const allMenuIds = []
        templateTree.value.forEach(activity => {
          if (activity.children && activity.children.length > 0) {
            activity.children.forEach(menu => {
              allMenuIds.push(menu.id)
            })
          }
        })
        activeMenuItems.value = allMenuIds
        isAllExpanded.value = true
      }
    }

    onMounted(() => {
      // 检查管理员权限
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user.is_admin !== 1) {
        ElMessage.error('无权限访问此页面')
        proxy.$router.push('/dashboard/scripts')
      }
    })

    return {
      selectedGame,
      templateTree,
      loading,
      dialogVisible,
      dialogTitle,
      isEditMode,
      saving,
      activeCollapseItems,
      materialInputVisible,
      materialInputValue,
      formData,
      formRules,
      loadTemplates,
      showAddActivityDialog,
      showAddMenuDialog,
      showAddProductDialog,
      showEditActivityDialog,
      showEditMenuDialog,
      showEditProductDialog,
      saveTemplate,
      deleteActivityConfirm,
      deleteMenuConfirm,
      deleteProductConfirm,
      activeMenuItems,
      formRef,
      materialInputRef,
      resetDialog,
      showMaterialInput,
      addMaterialName,
      removeMaterialName,
      toggleExpandAll,
      isAllExpanded
    }
  }
}
</script>

<style scoped>
.activity-craft-management {
  padding: 20px 20px 32px;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-info h2 {
  margin: 0 0 5px 0;
  font-size: 24px;
}

.header-desc {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.header-actions {
  display: flex;
  align-items: center;
}

.template-tree-container {
  background: #fff;
  border-radius: 4px;
  padding: 20px;
  min-height: 400px;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #999;
}

.template-tree {
  width: 100%;
}

.activity-item {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
}

:deep(.el-collapse-item__header) {
  padding: 0 20px;
  background-color: #f8f9fa;
  font-size: 16px;
  height: 56px;
  line-height: 56px;
}

:deep(.el-collapse-item__wrap) {
  border-bottom: none;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 0;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 16px;
}

.activity-name {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

.activity-actions {
  display: flex;
  gap: 12px;
}

.menus-list {
  padding: 16px;
  background-color: #fff;
}

.menu-item {
  margin-bottom: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
}

.menu-item:last-child {
  margin-bottom: 0;
}

:deep(.menu-item .el-collapse-item__header) {
  background-color: #fff;
  height: 48px;
  line-height: 48px;
  border-bottom: 1px solid #ebeef5;
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 16px;
}

.menu-name {
  font-weight: 500;
  font-size: 15px;
  color: #606266;
}

.menu-actions {
  display: flex;
  gap: 12px;
}

.products-list {
  padding: 16px;
  background-color: #fafafa;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
}

.product-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.product-item:last-child {
  margin-bottom: 0;
}

.product-info {
  flex: 1;
}

.product-name {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
  display: block;
  margin-bottom: 8px;
}

.product-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #909399;
}

.detail-item {
  display: flex;
  align-items: center;
  background-color: #f4f4f5;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #e9e9eb;
}

.product-actions {
  display: flex;
  gap: 12px;
  margin-left: 24px;
}

.no-products, .no-menus {
  text-align: center;
  color: #909399;
  padding: 32px 20px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px dashed #dcdfe6;
  margin: 16px;
}

.form-row {
  display: flex;
  gap: 20px;
  align-items: center;
}

.flex-1 {
  flex: 1;
}

/* 强制控制弹窗内控件的宽度 */
:deep(.custom-dialog .el-input),
:deep(.custom-dialog .el-select) {
  width: 300px !important;
}

:deep(.custom-dialog .el-input-number) {
  width: 150px !important;
}

/* 如果是单行排列的 row，取消宽度限制以适应 flex 布局 */
:deep(.custom-dialog .form-row .el-input-number),
:deep(.custom-dialog .form-row .el-switch) {
  width: auto !important;
}

:deep(.custom-dialog .el-dialog__body) {
  padding: 20px 30px;
}

:deep(.custom-dialog .el-form-item) {
  margin-bottom: 20px;
}

:deep(.custom-dialog .el-input-number .el-input__inner) {
  text-align: left;
}

.dialog-footer {
  text-align: right;
}
</style>
